import React, { createContext, useState, useContext, useEffect } from 'react';

const ThemeContext = createContext(null);

/**
 * Theme context provider for dark/light mode management.
 * Persists preference to localStorage and applies Bootstrap's data-bs-theme.
 *
 * @param {{ children: React.ReactNode }} props
 */
export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem('newsquasar-darkmode');
      return saved !== null ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('newsquasar-darkmode', JSON.stringify(darkMode));
    } catch {
      // localStorage unavailable
    }
    document.documentElement.setAttribute('data-bs-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  const toggleTheme = () => setDarkMode((prev) => !prev);

  return (
    <ThemeContext.Provider value={{ darkMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Hook to access theme context.
 * @returns {{ darkMode: boolean, toggleTheme: Function }}
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
